import { useState, useEffect, useRef, useCallback, MutableRefObject } from 'react';
import { Loader2, TestTube, UserCog, ScrollText, X, ChevronDown, Mail, Calendar, ArrowDownCircle, AlertCircle } from 'lucide-react';
import { EmailPreviewCard } from './EmailPreviewCard';
import { FloatingSaveBar } from './FloatingSaveBar';
import TestAgentModal from '../CallAgent/TestAgentModal';
import { AgentRulesSection } from './AgentRulesSection';
import SimpleCustomSelect from '../shared/SimpleCustomSelect';
import MultiSelectDropdown from '../shared/MultiSelectDropdown';
import { getAgentSettings, saveAgentIdentityConfig, generateAgentScript, getUserProfile, getAgentSettingsRow, upsertAgentSettingsRow, triggerReplyPromptGeneration, getConnectedEmailAccounts } from '../../services/settingsService';
import type { ConnectedEmailAccount } from '../../services/settingsService';
import { AgentAvatarUpload } from './AgentAvatarUpload';
import type { EmailAgentSettings as EmailAgentSettingsType, EmailTemplateDraft, EmailTemplateKey } from '../../types/settings';
import { EMAIL_TEMPLATE_DEFINITIONS } from '../../types/settings';
import { getEmailDrafts, getEmailTemplateStatuses, approveSingleEmailTemplate, saveEmailTemplatesAsDrafts } from '../../services/templateService';
import type { TemplateStatus } from '../../services/templateService';
import { DraftBanner } from './DraftBanner';
import { TEMPLATE_VARIABLES } from '../../hooks/useVariableInsertion';
import { COMMON_NATIONALITIES, COUNTRY_CAPITAL_MAP, COMMON_LOCATIONS, TONE_OPTIONS } from '../../shared/callAgentConstants';
import MessagingStyleTooltip from '../shared/MessagingStyleTooltip';
import { useProfile } from '../../contexts/ProfileContext';
import { useAuth } from '../../contexts/AuthContext';
import { canEditAgentSettings } from '../../utils/permissions';

const COLD_OUTREACH_TEMPLATES = EMAIL_TEMPLATE_DEFINITIONS.filter(t => t.category === 'cold_outreach');
const INBOUND_TEMPLATES = EMAIL_TEMPLATE_DEFINITIONS.filter(t => t.category === 'inbound');
const OTHER_TEMPLATES = EMAIL_TEMPLATE_DEFINITIONS.filter(t => t.category === 'other');

function EmailCategorySection({
  title,
  icon,
  templates,
  settings,
  senderName,
  senderEmail,
  onUpdateTemplate,
  onGenerateSubject,
  onGenerateBody,
  onDirtyChange,
  configuredCount,
  totalCount,
  defaultOpen = true,
  templateStatuses,
  onApproveTemplate,
  approvingKey,
  readOnly = false,
}: {
  title: string;
  icon: React.ReactNode;
  templates: typeof EMAIL_TEMPLATE_DEFINITIONS;
  settings: EmailAgentSettingsType;
  senderName?: string;
  senderEmail?: string;
  onUpdateTemplate: (key: EmailTemplateKey, subject: string, body: string) => void;
  onGenerateSubject: (key: EmailTemplateKey, tone: string, length: string, currentContent: string) => Promise<string>;
  onGenerateBody: (key: EmailTemplateKey, tone: string, length: string, currentContent: string) => Promise<string>;
  onDirtyChange: (cardKey: string, isDirty: boolean) => void;
  configuredCount: number;
  totalCount: number;
  defaultOpen?: boolean;
  templateStatuses?: Record<string, TemplateStatus>;
  onApproveTemplate?: (key: EmailTemplateKey, subject: string, body: string) => Promise<void>;
  approvingKey?: string | null;
  readOnly?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-6 py-4 bg-gradient-to-r from-gray-50 to-white hover:from-gray-100/80 hover:to-gray-50/50 transition-all duration-200"
      >
        <div className="flex items-center gap-3">
          {icon}
          <h4 className="text-sm font-bold text-gray-900 tracking-widest uppercase font-sans">{title}</h4>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 border border-[#1875FF]/15">
            <span className="text-xs font-bold text-[#1875FF]">{configuredCount}</span>
            <span className="text-xs text-gray-400">/</span>
            <span className="text-xs font-medium text-gray-500">{totalCount}</span>
          </div>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="px-6 pb-6 pt-2 space-y-3">
          {templates.map((tmpl) => {
            const templateData = settings.templates?.[tmpl.key];
            return (
              <EmailPreviewCard
                key={tmpl.key}
                title={`Email #${tmpl.number}: ${tmpl.label}`}
                subject={templateData?.subject || ''}
                content={templateData?.body || ''}
                senderName={senderName}
                senderEmail={senderEmail}
                subjectPlaceholder={tmpl.subjectPlaceholder}
                placeholder={tmpl.bodyPlaceholder}
                onUpdate={(subject, body) => onUpdateTemplate(tmpl.key, subject, body)}
                onGenerateSubject={(tone, length, currentContent) => onGenerateSubject(tmpl.key, tone, length, currentContent)}
                onGenerateBody={(tone, length, currentContent) => onGenerateBody(tmpl.key, tone, length, currentContent)}
                onDirtyChange={(isDirty) => onDirtyChange(tmpl.key, isDirty)}
                maxLength={2000}
                variables={TEMPLATE_VARIABLES}
                templateStatus={templateStatuses?.[tmpl.key]}
                onApproveTemplate={!readOnly && onApproveTemplate ? async (subject, body) => {
                  await onApproveTemplate(tmpl.key, subject, body);
                } : undefined}
                approvingTemplate={approvingKey === tmpl.key}
                readOnly={readOnly}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

interface EmailAgentSettingsProps {
  onDirtyStateChange?: (dirty: boolean) => void;
  saveRef?: MutableRefObject<(() => Promise<void>) | null>;
  discardRef?: MutableRefObject<(() => void) | null>;
}

export function EmailAgentSettings({ onDirtyStateChange, saveRef, discardRef }: EmailAgentSettingsProps) {
  const { profile } = useProfile();
  const { user } = useAuth();
  const canEdit = canEditAgentSettings(user);
  const [settings, setSettings] = useState<EmailAgentSettingsType | null>(null);
  const [originalSettings, setOriginalSettings] = useState<EmailAgentSettingsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | undefined>();
  const [showTestModal, setShowTestModal] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasLocalEdits, setHasLocalEdits] = useState(false);
  const dirtyCardsRef = useRef(new Set<string>());
  const [drafts, setDrafts] = useState<EmailTemplateDraft[]>([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [templateStatuses, setTemplateStatuses] = useState<Record<string, TemplateStatus>>({});
  const [approvingKey, setApprovingKey] = useState<string | null>(null);
  const [agentAvatarUrl, setAgentAvatarUrl] = useState<string | null>(null);
  const [nationalityInput, setNationalityInput] = useState('');
  const [showNationalitySuggestions, setShowNationalitySuggestions] = useState(false);
  const [locationInput, setLocationInput] = useState('');
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [locationInitialized, setLocationInitialized] = useState(false);
  const [emailAccounts, setEmailAccounts] = useState<ConnectedEmailAccount[]>([]);
  const [emailAccountsLoading, setEmailAccountsLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      await loadSettings();
      loadDrafts();
    };
    init();
    getAgentSettingsRow('email').then(row => {
      if (row?.avatar_url) setAgentAvatarUrl(row.avatar_url);
    });
    setEmailAccountsLoading(true);
    getConnectedEmailAccounts().then(accounts => {
      setEmailAccounts(accounts);
      setEmailAccountsLoading(false);
    });
  }, []);

  const handleCardDirtyChange = useCallback((cardKey: string, isDirty: boolean) => {
    if (isDirty) {
      dirtyCardsRef.current.add(cardKey);
    } else {
      dirtyCardsRef.current.delete(cardKey);
    }
    setHasLocalEdits(dirtyCardsRef.current.size > 0);
  }, []);

  useEffect(() => {
    if (settings && originalSettings) {
      const changed = JSON.stringify(settings) !== JSON.stringify(originalSettings) || hasLocalEdits;
      setHasChanges(changed);
      onDirtyStateChange?.(changed);
    }
  }, [settings, originalSettings, hasLocalEdits]);

  useEffect(() => {
    return () => { onDirtyStateChange?.(false); };
  }, []);

  useEffect(() => {
    if (!emailAccountsLoading && settings && settings.email_connection_id && !settings.gender) {
      const account = emailAccounts.find(a => a.id === settings.email_connection_id);
      if (account?.agent_gender) {
        const updated = { ...settings, gender: account.agent_gender };
        setSettings(updated);
        setOriginalSettings(prev => prev ? { ...prev, gender: account.agent_gender } : prev);
      }
    }
  }, [emailAccountsLoading, emailAccounts, settings?.email_connection_id]);

  const loadSettings = async () => {
    setLoading(true);
    const data = await getAgentSettings('email');
    if (data && !data.location && !locationInitialized) {
      const profile = await getUserProfile();
      if (profile?.country && COUNTRY_CAPITAL_MAP[profile.country]) {
        data.location = COUNTRY_CAPITAL_MAP[profile.country];
      }
      setLocationInitialized(true);
    }
    if (data?.location) {
      setLocationInput(data.location);
    }
    setSettings(data);
    setOriginalSettings(JSON.parse(JSON.stringify(data)));
    setLoading(false);
  };


  const loadDrafts = async () => {
    setDraftsLoading(true);
    const emailDrafts = await getEmailDrafts();
    const statuses = await getEmailTemplateStatuses();
    setTemplateStatuses(statuses);

    if (emailDrafts.length > 0) {
      const hasFreshFromGeneration = emailDrafts.some(d => d.source === 'sales_materials');

      if (hasFreshFromGeneration) {
        setDrafts(emailDrafts);
      } else {
        setSettings(prev => {
          if (!prev) return prev;
          const currentTemplates = prev.templates || ({} as any);
          const newTemplates = { ...currentTemplates };
          for (const draft of emailDrafts) {
            const key = draft.template_key as EmailTemplateKey;
            if (EMAIL_TEMPLATE_DEFINITIONS.some(t => t.key === key) && statuses[key] === 'draft') {
              newTemplates[key] = {
                subject: draft.subject_text || '',
                body: draft.body_text || '',
              };
            }
          }
          const updated = { ...prev, mode: 'preset' as const, templates: newTemplates };
          setOriginalSettings(JSON.parse(JSON.stringify(updated)));
          return updated;
        });
        setDrafts([]);
      }
    } else {
      setDrafts([]);
    }
    setDraftsLoading(false);
  };

  const applyDrafts = () => {
    if (!settings || drafts.length === 0) return;

    const currentTemplates = settings.templates || ({} as any);
    const newTemplates = { ...currentTemplates };
    const newStatuses: Record<string, TemplateStatus> = { ...templateStatuses };

    for (const draft of drafts) {
      const key = draft.template_key as EmailTemplateKey;
      if (EMAIL_TEMPLATE_DEFINITIONS.some(t => t.key === key)) {
        newTemplates[key] = {
          subject: draft.subject_text || '',
          body: draft.body_text || '',
        };
        newStatuses[key] = 'draft';
      }
    }

    setTemplateStatuses(newStatuses);
    setSettings({
      ...settings,
      mode: 'preset',
      templates: newTemplates,
    });
    setDrafts([]);
  };

  const handleSave = async () => {
    if (!settings) return;

    setSaving(true);
    const success = await saveAgentIdentityConfig('email', settings);

    if (success) {
      const hasDraftTemplates = Object.values(templateStatuses).some(s => s === 'draft');
      if (hasDraftTemplates && settings.templates) {
        await saveEmailTemplatesAsDrafts(settings.templates as Record<string, { subject: string; body: string }>, templateStatuses);
      }

      setSaved(true);
      setLastSaved(new Date());
      setOriginalSettings(JSON.parse(JSON.stringify(settings)));
      setHasChanges(false);
      triggerReplyPromptGeneration('email');
      setTimeout(() => setSaved(false), 2000);
    }

    setSaving(false);
  };

  const handleApproveTemplate = async (key: EmailTemplateKey, subject: string, body: string) => {
    setApprovingKey(key);
    const success = await approveSingleEmailTemplate(key, subject, body);
    if (success) {
      setTemplateStatuses(prev => ({ ...prev, [key]: 'approved' }));
    }
    setApprovingKey(null);
  };

  const handleDiscard = () => {
    if (originalSettings) {
      setSettings(JSON.parse(JSON.stringify(originalSettings)));
      setHasChanges(false);
      onDirtyStateChange?.(false);
    }
  };

  useEffect(() => {
    if (saveRef) saveRef.current = handleSave;
    if (discardRef) discardRef.current = handleDiscard;
  });


  const handleUpdateTemplate = (key: EmailTemplateKey, subject: string, body: string) => {
    if (!settings) return;
    const currentTemplates = settings.templates || ({} as any);
    setSettings({
      ...settings,
      templates: {
        ...currentTemplates,
        [key]: { subject, body },
      },
    });
  };

  const handleGenerateSubject = async (key: EmailTemplateKey, tone: string, length: string, currentContent?: string): Promise<string> => {
    try {
      const profile = await getUserProfile();
      const response = await generateAgentScript({
        agent_type: 'email',
        section: `${key}_subject`,
        company_name: profile?.company_name || undefined,
        tone,
        length,
        user_id: user?.email,
        client_id: user?.client_id,
        current_content: currentContent || undefined,
      });

      if (response?.generated_script) {
        return response.generated_script;
      }
      throw new Error('No script generated');
    } catch (error) {
      console.error('Error generating subject:', error);
      throw error;
    }
  };

  const handleGenerateBody = async (key: EmailTemplateKey, tone: string, length: string, currentContent?: string): Promise<string> => {
    try {
      const profile = await getUserProfile();
      const response = await generateAgentScript({
        agent_type: 'email',
        section: `${key}_body`,
        company_name: profile?.company_name || undefined,
        tone,
        length,
        user_id: user?.email,
        client_id: user?.client_id,
        current_content: currentContent || undefined,
      });

      if (response?.generated_script) {
        return response.generated_script;
      }
      throw new Error('No script generated');
    } catch (error) {
      console.error('Error generating email body:', error);
      throw error;
    }
  };

  const getConfiguredCount = (templates: typeof EMAIL_TEMPLATE_DEFINITIONS) => {
    return templates.filter(t => templateStatuses[t.key] === 'approved').length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Failed to load settings</p>
      </div>
    );
  }

  const resolvedSenderName = settings.agent_name?.trim() || profile?.full_name?.trim() || 'Adam Ray';
  const resolvedSenderEmail = settings.from_email?.trim() || profile?.email_address?.trim() || 'adam@salespad.app';

  return (
    <div className={`max-w-full ${hasChanges || saving || saved ? 'pb-28' : ''}`}>
      <div className="mb-6 flex items-start justify-between">
        <div className="flex items-center gap-4">
          <AgentAvatarUpload
            agentType="email"
            avatarUrl={agentAvatarUrl}
            onAvatarChange={setAgentAvatarUrl}
          />
          <div>
            <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display mb-2">Email Agent</h2>
            <p className="text-gray-600">
              Configure your AI email agent's templates and behavior
            </p>
          </div>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowTestModal(true)}
            className="px-4 py-2.5 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all flex items-center gap-2 font-semibold"
          >
            <TestTube className="w-5 h-5" />
            Test Agent
          </button>
        )}
      </div>

      {!canEdit && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-900 mb-1">View Only</p>
            <p className="text-xs text-blue-800">
              Only admins can edit agent settings. Contact your admin to make changes.
            </p>
          </div>
        </div>
      )}

      <DraftBanner
        draftCount={drafts.length}
        agentType="Email"
        loading={draftsLoading}
        onApplyDrafts={applyDrafts}
        onDismissDrafts={() => setDrafts([])}
      />

      <div className={`space-y-6 ${!canEdit ? 'pointer-events-none opacity-75' : ''}`}>
        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg">
              <UserCog className="w-5 h-5 text-[#1875FF]" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900 font-display">Agent Identity & Behavior</h3>
              <p className="text-sm text-gray-500">Configure your email agent's identity and sending details</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {/* Left Column */}
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Agent Account</label>
                {emailAccountsLoading ? (
                  <div className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-400 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading accounts...
                  </div>
                ) : emailAccounts.length === 0 ? (
                  <div className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-400 bg-gray-50">
                    No accounts connected
                  </div>
                ) : (
                  <SimpleCustomSelect
                    value={settings.email_connection_id || ''}
                    onChange={(value) => {
                      const account = emailAccounts.find(a => a.id === value);
                      if (account) {
                        const lines = settings.signature.split('\n');
                        lines[1] = account.display_name || '';
                        setSettings({
                          ...settings,
                          email_connection_id: account.id,
                          agent_name: account.display_name || '',
                          from_email: account.email || '',
                          gender: account.agent_gender || '',
                          signature: lines.join('\n'),
                        });
                      }
                    }}
                    options={emailAccounts.map(a => ({
                      value: a.id,
                      label: `${a.display_name} (${a.email})`,
                    }))}
                    placeholder="Select account"
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                <div className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700 min-h-[42px] flex items-center">
                  {settings.gender ? settings.gender.charAt(0).toUpperCase() + settings.gender.slice(1) : ''}
                </div>
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nationality
                  {(settings.nationality?.length || 0) > 0 && (
                    <span className="ml-2 text-xs text-gray-400 font-normal">
                      {settings.nationality.length}/3
                    </span>
                  )}
                </label>
                <div
                  className="w-full min-h-[42px] px-3 py-1.5 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl focus-within:ring-2 focus-within:ring-[#1875FF]/50 focus-within:border-[#1875FF] text-sm transition-all hover:border-[#1875FF]/40 hover:shadow-lg hover:shadow-[#1875FF]/10 flex flex-wrap items-center gap-1.5 cursor-text"
                  onClick={() => {
                    const inp = document.getElementById('email-nationality-typeahead');
                    if (inp) inp.focus();
                  }}
                >
                  {(settings.nationality || []).map(n => (
                    <span
                      key={n}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#1875FF]/10 text-[#1875FF] rounded-md text-xs font-semibold whitespace-nowrap flex-shrink-0"
                    >
                      {n}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSettings({
                            ...settings,
                            nationality: settings.nationality.filter(v => v !== n),
                          });
                        }}
                        className="hover:bg-[#1875FF]/20 rounded-full p-0.5 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {(settings.nationality?.length || 0) < 3 ? (
                    <input
                      id="email-nationality-typeahead"
                      type="text"
                      value={nationalityInput}
                      onChange={(e) => {
                        setNationalityInput(e.target.value);
                        setShowNationalitySuggestions(e.target.value.length > 0);
                      }}
                      onFocus={() => nationalityInput.length > 0 && setShowNationalitySuggestions(true)}
                      onBlur={() => setTimeout(() => setShowNationalitySuggestions(false), 200)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ',') {
                          e.preventDefault();
                          const trimmed = nationalityInput.trim();
                          if (trimmed && !(settings.nationality || []).includes(trimmed)) {
                            setSettings({
                              ...settings,
                              nationality: [...(settings.nationality || []), trimmed],
                            });
                          }
                          setNationalityInput('');
                          setShowNationalitySuggestions(false);
                        }
                        if (e.key === 'Backspace' && !nationalityInput && (settings.nationality || []).length > 0) {
                          setSettings({
                            ...settings,
                            nationality: settings.nationality.slice(0, -1),
                          });
                        }
                      }}
                      placeholder={(settings.nationality || []).length === 0 ? 'Type nationality...' : ''}
                      className="flex-1 min-w-[80px] outline-none bg-transparent text-sm font-medium py-0.5"
                    />
                  ) : (
                    <span className="text-xs text-gray-400 py-0.5">Max 3 reached</span>
                  )}
                </div>
                {showNationalitySuggestions && (() => {
                  const filtered = COMMON_NATIONALITIES.filter(
                    n => n.toLowerCase().includes(nationalityInput.toLowerCase()) &&
                      !(settings.nationality || []).includes(n)
                  );
                  return filtered.length > 0 ? (
                    <div className="absolute z-10 mt-1 w-full bg-white border-2 border-gray-200 rounded-xl shadow-2xl shadow-[#1875FF]/20 max-h-48 overflow-auto">
                      {filtered.map((nat, idx) => (
                        <button
                          key={nat}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            if (!(settings.nationality || []).includes(nat)) {
                              setSettings({
                                ...settings,
                                nationality: [...(settings.nationality || []), nat],
                              });
                            }
                            setNationalityInput('');
                            setShowNationalitySuggestions(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-[#1875FF]/5 hover:text-[#1875FF] transition-colors font-medium ${idx > 0 ? 'border-t border-gray-100' : ''}`}
                        >
                          {nat}
                        </button>
                      ))}
                    </div>
                  ) : null;
                })()}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Languages</label>
                <MultiSelectDropdown
                  value={settings.languages || ['english']}
                  onChange={(values) => setSettings({ ...settings, languages: values.includes('english') ? values : ['english', ...values] })}
                  options={[
                    { value: 'english', label: 'English' },
                    { value: 'french', label: 'French' },
                    { value: 'spanish', label: 'Spanish' },
                    { value: 'italian', label: 'Italian' },
                    { value: 'arabic', label: 'Arabic' },
                    { value: 'russian', label: 'Russian' },
                  ]}
                  placeholder="Select languages"
                  pinnedValues={['english']}
                />
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                <input
                  type="text"
                  value={locationInput}
                  onChange={(e) => {
                    setLocationInput(e.target.value);
                    setShowLocationSuggestions(e.target.value.length > 0);
                    setSettings({ ...settings, location: e.target.value });
                  }}
                  onFocus={() => locationInput.length > 0 && setShowLocationSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowLocationSuggestions(false), 200)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setShowLocationSuggestions(false);
                    }
                  }}
                  placeholder="City, Country"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm transition-all"
                />
                {showLocationSuggestions && (() => {
                  const filtered = COMMON_LOCATIONS.filter(
                    loc => loc.toLowerCase().includes(locationInput.toLowerCase())
                  );
                  return filtered.length > 0 ? (
                    <div className="absolute z-10 mt-1 w-full bg-white border-2 border-gray-200 rounded-xl shadow-2xl shadow-[#1875FF]/20 max-h-48 overflow-auto">
                      {filtered.map((loc, idx) => (
                        <button
                          key={loc}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setLocationInput(loc);
                            setSettings({ ...settings, location: loc });
                            setShowLocationSuggestions(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-[#1875FF]/5 hover:text-[#1875FF] transition-colors font-medium ${idx > 0 ? 'border-t border-gray-100' : ''}`}
                        >
                          {loc}
                        </button>
                      ))}
                    </div>
                  ) : null;
                })()}
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <input
                  type="text"
                  value={settings.role || ''}
                  onChange={(e) => setSettings({ ...settings, role: e.target.value })}
                  placeholder="Sales Consultant"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Objective</label>
                <input
                  type="text"
                  value={settings.objective || ''}
                  onChange={(e) => setSettings({ ...settings, objective: e.target.value })}
                  placeholder="Book a meeting"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm transition-all"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  Writing Style
                  <MessagingStyleTooltip agentType="email" />
                </label>
                <SimpleCustomSelect
                  value={settings.tone || 'professional'}
                  onChange={(value) => setSettings({ ...settings, tone: value })}
                  options={TONE_OPTIONS}
                  placeholder="Select style"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Length & Layout</label>
                <SimpleCustomSelect
                  value={settings.email_layout || 'standard'}
                  onChange={(value) => setSettings({ ...settings, email_layout: value })}
                  options={[
                    { value: 'short', label: 'Short & Concise' },
                    { value: 'standard', label: 'Standard' },
                    { value: 'detailed', label: 'Long & Detailed' },
                  ]}
                  placeholder="Select layout"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Emails Per Lead</label>
                <input
                  type="number"
                  value={settings.max_emails}
                  onChange={(e) => {
                    const val = Math.min(Math.max(parseInt(e.target.value) || 5, 5), 8);
                    setSettings({ ...settings, max_emails: val });
                  }}
                  min="5"
                  max="8"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm transition-all"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`mt-6 space-y-6 ${!canEdit ? 'opacity-75' : ''}`}>
        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg">
              <ScrollText className="w-5 h-5 text-[#1875FF]" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900 font-display">Email Templates</h3>
              <p className="text-sm text-gray-500">Craft the email sequences your agent sends</p>
            </div>
          </div>

          <div className="space-y-5">
            <EmailCategorySection
              title="Cold Outreach"
              icon={<Mail className="w-5 h-5 text-[#1875FF]" />}
              templates={COLD_OUTREACH_TEMPLATES}
              settings={settings}
              senderName={resolvedSenderName}
              senderEmail={resolvedSenderEmail}
              onUpdateTemplate={handleUpdateTemplate}
              onGenerateSubject={handleGenerateSubject}
              onGenerateBody={handleGenerateBody}
              onDirtyChange={handleCardDirtyChange}
              configuredCount={getConfiguredCount(COLD_OUTREACH_TEMPLATES)}
              totalCount={COLD_OUTREACH_TEMPLATES.length}
              defaultOpen={false}
              templateStatuses={templateStatuses}
              onApproveTemplate={handleApproveTemplate}
              approvingKey={approvingKey}
              readOnly={!canEdit}
            />

            <EmailCategorySection
              title="Inbound"
              icon={<ArrowDownCircle className="w-5 h-5 text-emerald-500" />}
              templates={INBOUND_TEMPLATES}
              settings={settings}
              senderName={resolvedSenderName}
              senderEmail={resolvedSenderEmail}
              onUpdateTemplate={handleUpdateTemplate}
              onGenerateSubject={handleGenerateSubject}
              onGenerateBody={handleGenerateBody}
              onDirtyChange={handleCardDirtyChange}
              configuredCount={getConfiguredCount(INBOUND_TEMPLATES)}
              totalCount={INBOUND_TEMPLATES.length}
              defaultOpen={false}
              templateStatuses={templateStatuses}
              onApproveTemplate={handleApproveTemplate}
              approvingKey={approvingKey}
              readOnly={!canEdit}
            />

            <EmailCategorySection
              title="Other"
              icon={<Calendar className="w-5 h-5 text-[#05E0D1]" />}
              templates={OTHER_TEMPLATES}
              settings={settings}
              senderName={resolvedSenderName}
              senderEmail={resolvedSenderEmail}
              onUpdateTemplate={handleUpdateTemplate}
              onGenerateSubject={handleGenerateSubject}
              onGenerateBody={handleGenerateBody}
              onDirtyChange={handleCardDirtyChange}
              configuredCount={getConfiguredCount(OTHER_TEMPLATES)}
              totalCount={OTHER_TEMPLATES.length}
              defaultOpen={false}
              templateStatuses={templateStatuses}
              onApproveTemplate={handleApproveTemplate}
              approvingKey={approvingKey}
              readOnly={!canEdit}
            />
          </div>
        </div>

        <div className={!canEdit ? 'pointer-events-none' : ''}>
          <AgentRulesSection
            doRules={settings.do_rules}
            dontRules={settings.dont_rules}
            onDoRulesChange={(rules) => setSettings({ ...settings, do_rules: rules })}
            onDontRulesChange={(rules) => setSettings({ ...settings, dont_rules: rules })}
            subtitle="Define what your agent should and shouldn't do in emails"
          />
        </div>
      </div>

      {canEdit && (
        <FloatingSaveBar
          hasChanges={hasChanges}
          saving={saving}
          saved={saved}
          lastSaved={lastSaved}
          onSave={handleSave}
          onDiscard={handleDiscard}
        />
      )}

      <TestAgentModal
        isOpen={showTestModal}
        onClose={() => setShowTestModal(false)}
        agentType="email"
      />
    </div>
  );
}
